import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ShippingPolicyDisplay from '@/components/ui-v2/shipping-policy/free-shipping-configurator';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { DealItemWithVersions } from '@/lib/graphql/generated/graphql';
import { useShippingPolicy } from '@/lib/hooks/useShippingPolicy';
import { ShippingPolicy } from '@/lib/types/shipping-policy';
import {
  formatMinimumOrderStartDate,
  offerDurationToDays,
} from '@/lib/types/shipping-policy';
import { cn, formatCurrency } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import Button from '@appchoose/button';
import { useAtomValue } from 'jotai';
import { Clock, Edit } from 'lucide-react';

interface FreeShippingBlockProps {
  dealId: string;
  dealItems: DealItemWithVersions[];
  hubspotStartDate?: Date | null;
  disabled?: boolean;
}

function FreeShippingBlock({
  dealId,
  dealItems,
  hubspotStartDate,
  disabled = false,
}: FreeShippingBlockProps) {
  const { t, i18n } = useTranslation();
  const store = useAtomValue(storeAtom);
  const isOfferLocked = useIsOfferLocked();
  const { shippingPolicy, handleShippingPolicyChange } =
    useShippingPolicy(dealId);
  const [configModalOpen, setConfigModalOpen] = useState(false);

  // Determine if policy is active
  const isPolicyActive =
    shippingPolicy.minimumOrderEnabled ||
    shippingPolicy.timeLimitedEnabled ||
    shippingPolicy.allShippingFeesZero;
  const bothOptionsEnabled =
    shippingPolicy.minimumOrderEnabled && shippingPolicy.timeLimitedEnabled;

  const isDisabled = disabled || shippingPolicy.allShippingFeesZero;

  const getEndDate = (): string | null => {
    if (!shippingPolicy.timeLimitedEnabled || !shippingPolicy.offerDuration) {
      return null;
    }

    const startDate = hubspotStartDate
      ? new Date(hubspotStartDate)
      : new Date();
    const days = offerDurationToDays(shippingPolicy.offerDuration);
    if (!days) return null;

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + days);

    return endDate.toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
    });
  };

  const getStartDate = (): string | null => {
    if (!shippingPolicy.minimumOrderEnabled) {
      return null;
    }

    return formatMinimumOrderStartDate(
      hubspotStartDate,
      shippingPolicy.timeLimitedEnabled,
      shippingPolicy.offerDuration,
      i18n.language
    );
  };

  const endDate = getEndDate();
  const startDate = getStartDate();

  return (
    <>
      <section
        onClick={() => !isOfferLocked && setConfigModalOpen(true)}
        className={cn(
          'flex items-center justify-between gap-4 rounded-lg border border-neutral-200 p-6 transition-colors',
          isOfferLocked
            ? 'cursor-not-allowed opacity-50'
            : !isDisabled
              ? 'cursor-pointer hover:bg-neutral-50'
              : ''
        )}
      >
        <div className="flex-1">
          <div className="mb-4 flex items-center gap-3">
            <h2 className="text-lg font-semibold">
              {t('ui-v2.settings.blocks-shipping-policy.free-shipping.title')}
            </h2>
            <span
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-medium',
                isPolicyActive
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              )}
            >
              {isPolicyActive
                ? t('ui-v2.settings.blocks-shipping-policy.status-active')
                : t('ui-v2.settings.blocks-shipping-policy.status-inactive')}
            </span>
          </div>

          {isPolicyActive && !shippingPolicy.allShippingFeesZero && (
            <div className="space-y-8">
              {/* Phase 1: Time Limited */}
              {shippingPolicy.timeLimitedEnabled &&
                shippingPolicy.offerDuration && (
                  <div className="space-y-2 border-l border-l-gray-300 pl-4">
                    <p className="text-sm text-muted-foreground">
                      {bothOptionsEnabled
                        ? `${t('ui-v2.settings.blocks-shipping-policy.free-shipping.phase-1')} : ${t('ui-v2.settings.blocks-shipping-policy.free-shipping.duration-label')}`
                        : t(
                            'ui-v2.settings.blocks-shipping-policy.free-shipping.duration-label'
                          )}
                    </p>
                    <p className="text-xl font-bold text-foreground">
                      {shippingPolicy.offerDuration}
                    </p>
                    {endDate && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <span>
                          {t(
                            'ui-v2.settings.blocks-shipping-policy.free-shipping.ends-on-at',
                            { date: endDate }
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}

              {/* Phase 2: Minimum Order Amount */}
              {shippingPolicy.minimumOrderEnabled &&
                shippingPolicy.minimumOrderAmount && (
                  <div className="space-y-2 border-l border-l-gray-300 pl-4">
                    <p className="text-sm text-muted-foreground">
                      {bothOptionsEnabled
                        ? `${t('ui-v2.settings.blocks-shipping-policy.free-shipping.phase-2')} : ${t('components.other.min_purchase_amount')}`
                        : t('components.other.min_purchase_amount')}
                    </p>
                    <p className="text-xl font-bold text-foreground">
                      {formatCurrency(shippingPolicy.minimumOrderAmount, store)}
                    </p>
                    {/* Only show start date if both options are enabled (Phase 2 starts after Phase 1) */}
                    {bothOptionsEnabled && startDate && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        <span>
                          {t(
                            'ui-v2.settings.blocks-shipping-policy.free-shipping.starts-on-at',
                            { date: startDate }
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}
            </div>
          )}

          {!isPolicyActive && (
            <p className="text-sm text-muted-foreground">
              {t(
                'ui-v2.settings.blocks-shipping-policy.free-shipping.no-policy'
              )}
            </p>
          )}

          {shippingPolicy.allShippingFeesZero && (
            <p className="text-sm text-muted-foreground">
              {t(
                'ui-v2.settings.blocks-shipping-policy.free-shipping.no-conditions.description',
                { currency: formatCurrency(0, store) }
              )}
              {t(
                'ui-v2.settings.blocks-shipping-policy.free-shipping.no-conditions.to_disable'
              )}
            </p>
          )}
        </div>

        {!isDisabled && (
          <Button
            type="button"
            appearance="outline"
            containsIcon={true}
            onClick={(e) => {
              e.stopPropagation();
              if (!isOfferLocked) {
                setConfigModalOpen(true);
              }
            }}
            disabled={isOfferLocked}
            className="gap-2 border-0"
          >
            <Edit className="h-4 w-4" />
            {t('ui-v2.settings.blocks-shipping-policy.action-update')}
          </Button>
        )}
      </section>

      <ShippingPolicyDisplay
        policy={shippingPolicy}
        onPolicyChange={async (newPolicy: ShippingPolicy) => {
          await handleShippingPolicyChange(newPolicy);
          setConfigModalOpen(false);
        }}
        isLocked={isOfferLocked}
        dealItems={dealItems}
        open={configModalOpen && !isDisabled}
        onOpenChange={setConfigModalOpen}
        hubspotStartDate={hubspotStartDate}
      />
    </>
  );
}

export default FreeShippingBlock;
